import { tagAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

import { rem } from '../themeUtils';

const tagSlotRecipe = defineSlotRecipe({
  className: 'tag',
  slots: tagAnatomy.keys(),
  base: {
    root: {
      borderRadius: '4',
      border: '1px solid',
      display: 'inline-flex',
      alignItems: 'center',
      overflow: 'hidden',
      maxWidth: '100%',
    },
    // The root has a fixed height, so a label that wraps gets cut off mid-line instead of growing
    // the tag. Keep it on one line and let it end in an ellipsis. `truncate` sets no `display`, and
    // only `block` takes the width the flex parent shrank the label to (see Breadcrumb.recipe.ts);
    // `minWidth` is what allows that shrink in the first place.
    label: {
      display: 'block',
      minWidth: 0,
      truncate: true,
    },
  },
  compoundVariants: [
    {
      colorVariant: 'neutral',
      disabled: false,
      css: {
        root: {
          color: 'text/primary',
        },
      },
    },
  ],
  variants: {
    // The close button in endElement hovers to `subtle` in its own recipe, which is exactly the
    // tag's background, so each palette re-points its hover one step darker.
    colorVariant: {
      neutral: {
        root: {
          backgroundColor: 'color/neutral/subtle',
          borderColor: 'color/neutral/muted',
          color: 'color/neutral/strong',
        },
        endElement: { _hover: { backgroundColor: 'color/neutral/moderate' } },
      },
      blue: {
        root: { backgroundColor: 'color/blue/subtle', borderColor: 'color/blue/muted', color: 'color/blue/strong' },
        endElement: { _hover: { backgroundColor: 'color/blue/moderate' } },
      },
      green: {
        root: { backgroundColor: 'color/green/subtle', borderColor: 'color/green/muted', color: 'color/green/strong' },
        endElement: { _hover: { backgroundColor: 'color/green/moderate' } },
      },
      red: {
        root: { backgroundColor: 'color/red/subtle', borderColor: 'color/red/muted', color: 'color/red/strong' },
        endElement: { _hover: { backgroundColor: 'color/red/moderate' } },
      },
      yellow: {
        root: {
          backgroundColor: 'color/yellow/subtle',
          borderColor: 'color/yellow/muted',
          color: 'color/yellow/strong',
        },
        endElement: { _hover: { backgroundColor: 'color/yellow/moderate' } },
      },
      purple: {
        root: {
          backgroundColor: 'color/purple/subtle',
          borderColor: 'color/purple/muted',
          color: 'color/purple/strong',
        },
        endElement: { _hover: { backgroundColor: 'color/purple/moderate' } },
      },
    },
    disabled: {
      true: {
        root: {
          backgroundColor: 'background/disabled',
          borderColor: 'border/disabled',
          color: 'text/on-disabled',
        },
      },
      false: {},
    },
    size: {
      sm: {
        root: {
          height: '24',
          paddingInline: '4',
          '&:has(button)': {
            paddingInlineEnd: '1',
          },
        },
        label: {
          paddingInline: '4',
          textStyle: 'comp/tag/sm',
        },
        endElement: {
          width: '20',
          height: '20',
        },
      },
      md: {
        root: {
          height: '32',
          paddingInline: '6',
          '&:has(button)': {
            paddingInlineEnd: rem(3),
          },
        },
        startElement: {
          marginInlineEnd: '-2px',
        },
        label: {
          paddingInline: '6',
          textStyle: 'comp/tag/md',
        },
      },
    },
  },
  defaultVariants: {
    colorVariant: 'neutral',
    size: 'md',
  },
});

export default tagSlotRecipe;
